/**
 * `/demo/raster-cog` — proves GeoTIFF/COG → TiTiler → MapLibre without gTwin.
 *
 * The browser never decodes TIFF; TiTiler serves WebMercatorQuad PNG tiles.
 * Prerequisites: TiTiler running (default http://localhost:8000) or set
 * `VITE_TILES_URL` (optionally `http://127.0.0.1:5173/titiler` with Vite proxy).
 *
 * Override default sample with `VITE_DEMO_COG_URL` (HTTPS URL TiTiler can fetch).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ViewerHost } from '@geosoft/viewer-sdk';
import { MapLibreAdapter } from '@/engines/maplibre/MapLibreAdapter';
import { CanvasMapAdapter } from '@/engines/openlayers/CanvasMapAdapter';
import { sceneItemToLayer } from '@/app/sceneItemToLayer';
import { Banner, tokens } from '@/design';
import { BasemapSwitcher } from '@/components/map/BasemapSwitcher';

/** Small public GeoTIFF sample (RGB); TiTiler reads it over HTTPS. */
const DEFAULT_DEMO_COG =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEMO_COG_URL) ||
  'https://download.osgeo.org/geotiff/samples/senate.tif';

const TILES_BASE = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TILES_URL) ||
  'http://localhost:8000'
).replace(/\/+$/, '');

interface CogTileJson {
  bounds?: [number, number, number, number];
  center?: [number, number, number?];
}

function buildCogTileJsonUrl(cogUrl: string): string {
  const url = new URL(`${TILES_BASE}/cog/WebMercatorQuad/tilejson.json`);
  url.searchParams.set('url', cogUrl);
  url.searchParams.append('bidx', '1');
  url.searchParams.append('bidx', '2');
  url.searchParams.append('bidx', '3');
  return url.toString();
}

export function RasterDemoRoute() {
  const [searchParams] = useSearchParams();
  const urlFromQuery = searchParams.get('url')?.trim();

  const containerRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<ViewerHost | null>(null);
  const mountedRef = useRef(false);
  const [cogUrl, setCogUrl] = useState(() => urlFromQuery || DEFAULT_DEMO_COG);
  const [tileJson, setTileJson] = useState<CogTileJson | null>(null);
  const [status, setStatus] = useState<string>('Mount map…');
  const [hostGen, setHostGen] = useState(0);

  useEffect(() => {
    const u = searchParams.get('url')?.trim();
    if (u) setCogUrl(u);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const trimmed = cogUrl.trim();
    if (!trimmed) return;

    setStatus('Reading COG bounds from TiTiler…');
    void (async () => {
      try {
        const res = await fetch(buildCogTileJsonUrl(trimmed));
        if (!res.ok) throw new Error(`TiTiler tilejson failed: HTTP ${res.status}`);
        const json = (await res.json()) as CogTileJson;
        if (!cancelled) {
          setTileJson(json);
          setStatus('COG bounds loaded — adding raster layer…');
        }
      } catch (e) {
        if (!cancelled) {
          setTileJson(null);
          setStatus(e instanceof Error ? e.message : String(e));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cogUrl]);

  const demoLayer = useMemo(() => {
    const bbox = tileJson?.bounds ?? [-77.05, 38.87, -77.0, 38.92];
    const layer = sceneItemToLayer(
      {
        id: 'demo-geotiff-cog',
        layerKind: 'cog',
        crs: 'EPSG:4326',
        bbox,
        endpoints: { cog: cogUrl.trim() },
      },
      'realityData',
    );
    layer.label = 'Demo GeoTIFF (COG via TiTiler)';
    return layer;
  }, [cogUrl, tileJson]);

  useEffect(() => {
    let cancelled = false;
    if (mountedRef.current || !containerRef.current) return;
    mountedRef.current = true;

    void (async () => {
      try {
        let host: ViewerHost;
        try {
          host = new ViewerHost({
            container: containerRef.current!,
            primaryEngine: 'maplibre',
            adapters: [new MapLibreAdapter()],
          });
          await host.mount();
        } catch (webglErr) {
          console.warn('[RasterDemoRoute] MapLibre WebGL failed — Canvas 2D fallback', webglErr);
          host = new ViewerHost({
            container: containerRef.current!,
            primaryEngine: 'openlayers',
            adapters: [new CanvasMapAdapter()],
          });
          await host.mount();
        }
        if (cancelled) {
          await host.unmount();
          return;
        }
        hostRef.current = host;
        setHostGen((n) => n + 1);
        setStatus('Map ready — loading raster tiles…');
      } catch (e) {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      void hostRef.current?.unmount();
      hostRef.current = null;
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || hostGen === 0) return;
    void (async () => {
      try {
        setStatus('Adding COG layer…');
        for (const existing of host.listLayers()) {
          await host.removeLayer(existing.id);
        }
        await host.addLayer(demoLayer);
        const center = tileJson?.center;
        if (center) {
          host.flyTo({ lonLat: [center[0], center[1]], zoom: Math.max(center[2] ?? 11, 12) });
        } else {
          host.flyTo({ lonLat: [-77.024, 38.892], zoom: 15 });
        }
        setStatus(
          'Layer added. If you see a blank map, start TiTiler and check tile requests in Network.',
        );
      } catch (e) {
        setStatus(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [demoLayer, hostGen, tileJson]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
      data-route="/demo/raster-cog"
    >
      {/*
       * WP-11: persistent developer banner. The masterplan §5.5
       * mandates that direct-file fallback routes never write to a
       * gTwin and must surface a clear notice that the surface
       * bypasses the project model. The banner is placed first in
       * source order so it precedes the demo chrome and is visible
       * to assistive technologies on the very first focus.
       */}
      <Banner
        severity="info"
        title="Developer demo — bypasses gTwin"
        body={
          <span>
            This route renders a single GeoTIFF / COG directly through TiTiler.
            It does not load or write to any <code>.gtwin</code> project. Open
            <Link
              to="/projects"
              style={{ color: tokens.color.accent.primary, marginLeft: tokens.space[1] }}
            >
              Projects
            </Link>
            {' '}to use the gTwin-first workflow.
          </span>
        }
        narrationKey="raster-demo-bypass-banner"
      />
      <header
        style={{
          flexShrink: 0,
          padding: tokens.space[3],
          borderBottom: tokens.border.thin,
          background: tokens.color.surface[1],
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[3], alignItems: 'center' }}>
          <strong style={{ fontSize: tokens.font.size.md }}>Raster demo (GeoTIFF / COG)</strong>
          <Link to="/projects" style={{ color: tokens.color.accent.primary, fontSize: tokens.font.size.sm }}>
            ← Projects
          </Link>
        </div>
        <p style={{ margin: `${tokens.space[2]}px 0 0`, fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
          TIFF is not rendered in the browser. TiTiler must run (e.g. port 8000) so XYZ PNG tiles load. Converter
          writeback must store <code style={{ fontSize: tokens.font.size.xs }}>endpoints.cog</code>, not raw{' '}
          <code style={{ fontSize: tokens.font.size.xs }}>tiles</code>, for the main viewer path.
        </p>
        <label style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.space[2], marginTop: tokens.space[3], alignItems: 'center' }}>
          <span style={{ fontSize: tokens.font.size.sm }}>COG / GeoTIFF URL (http(s), reachable by TiTiler)</span>
          <input
            type="url"
            value={cogUrl}
            onChange={(e) => setCogUrl(e.target.value)}
            style={{
              flex: `1 1 ${tokens.layout.popoverSm}px`,
              minHeight: tokens.density.touchMin,
              // `standaloneStateExtraWide` (720) is the largest tokenized
              // single-pane width; capping the URL field at it prevents
              // the input from stretching across an ultrawide monitor.
              maxWidth: `min(${tokens.layout.standaloneStateExtraWide}px, 100%)`,
              padding: tokens.space[2],
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
            }}
          />
        </label>
        <p style={{ margin: `${tokens.space[2]}px 0 0`, fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
          {status}
        </p>
      </header>
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <BasemapSwitcher container={containerRef.current} />
      </div>
    </div>
  );
}
